import type { GraphQLError } from 'graphql';
import { ClientError } from 'graphql-request';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

export interface GraphQLErrorDetail {
  message: string;
  errorType?: string;
  path?: (string | number)[];
}

export class GraphQLApiError extends Error {
  errors: GraphQLErrorDetail[];
  operation?: string;

  constructor(errors: GraphQLErrorDetail[], message?: string, operation?: string) {
    super(message ?? errors[0]?.message ?? 'GraphQL API error');
    this.name = 'GraphQLApiError';
    this.errors = errors;
    this.operation = operation;
  }
}

const describeFailure = (details: GraphQLErrorDetail[], operation?: string): string => {
  const reason = details.map((detail) => detail.message).join(' | ');
  return isNil(operation) ? reason : `GraphQL ${operation} failed: ${reason}`;
};

const hasGraphQLErrors = (error: unknown): error is { errors: GraphQLErrorDetail[] } =>
  !isNil(error) && typeof error === 'object' && Array.isArray((error as { errors?: unknown }).errors);

export const AUTHORIZER_TIMEOUT_MESSAGE = 'lambda invocation timed out';
export const HTTP_STATUS_UNAUTHORIZED = 401;

export const isUnauthorizedGraphQLError = (error: unknown): boolean => {
  if (isNil(error) || typeof error !== 'object') {
    return false;
  }
  const candidate = error as {
    message?: string;
    status?: number;
    response?: { status?: number; errors?: GraphQLErrorDetail[] };
    errors?: GraphQLErrorDetail[];
  };
  const details = candidate.errors ?? candidate.response?.errors ?? [];
  const status = candidate.response?.status ?? candidate.status;
  const isAuthRejection =
    status === HTTP_STATUS_UNAUTHORIZED || details.some((detail) => detail.errorType === 'UnauthorizedException');
  if (!isAuthRejection) {
    return false;
  }
  // Authorizer Lambda timeouts also surface as 401/UnauthorizedException but are transient,
  // not a real auth denial — same exclusion as the graphqlClient response middleware.
  const messages = [candidate.message ?? '', ...details.map((detail) => detail.message)];
  return !messages.some((message) => message.includes(AUTHORIZER_TIMEOUT_MESSAGE));
};

const CONDITIONAL_CHECK_FAILED_ERROR_TYPE = 'ConditionalCheckFailedException';
const CONDITIONAL_REQUEST_FAILED_MESSAGE = 'The conditional request failed';

export const isConditionalCheckFailedGraphQLError = (error: unknown): boolean => {
  if (isNil(error) || typeof error !== 'object') {
    return false;
  }
  const candidate = error as {
    message?: string;
    response?: { errors?: GraphQLErrorDetail[] };
    errors?: GraphQLErrorDetail[];
  };
  const details = candidate.errors ?? candidate.response?.errors ?? [];
  if (details.some((detail) => (detail.errorType ?? '').includes(CONDITIONAL_CHECK_FAILED_ERROR_TYPE))) {
    return true;
  }
  const messages = [candidate.message ?? '', ...details.map((detail) => detail.message)];
  return messages.some((message) => message.includes(CONDITIONAL_REQUEST_FAILED_MESSAGE));
};

const toErrorDetails = (errors: readonly GraphQLError[]): GraphQLErrorDetail[] =>
  errors.map((error) => ({
    message: error.message,
    errorType: (error as { errorType?: string }).errorType,
    path: isNil(error.path) ? undefined : [...error.path],
  }));

/**
 * Normalises whatever a GraphQL call rejected with into an `Error` safe to render and report.
 * `operation` is the GraphQL field that failed; when given, it opens the message so the error
 * reads `GraphQL <operation> failed: <reason>` instead of a bare reason.
 */
export const toGraphQLApiError = (error: unknown, operation?: string): Error => {
  // `graphql-request` builds ClientError.message as `<first error>: <JSON.stringify({ response, request })>`,
  // so rendering it raw dumps the whole query and its variables into the UI.
  if (error instanceof ClientError) {
    const details = toErrorDetails(error.response.errors ?? []);
    const message = isEmpty(details)
      ? `GraphQL Error (Code: ${String(error.response.status)})`
      : describeFailure(details, operation);
    return new GraphQLApiError(details, message, operation);
  }
  if (error instanceof Error) {
    return error;
  }
  if (hasGraphQLErrors(error)) {
    return new GraphQLApiError(error.errors, describeFailure(error.errors, operation), operation);
  }
  return new Error(String(error));
};
